(() => {
  const normalise = (value) => String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const questions = [
    {
      key: 'gender',
      title: 'Who is the fragrance for?',
      help: 'Choose the person you are shopping for.',
      options: [
        { value: 'men', label: 'Men' },
        { value: 'women', label: 'Women' },
        { value: 'unisex', label: 'Unisex' },
        { value: 'no-preference', label: 'No preference' }
      ]
    },
    {
      key: 'occasion',
      title: 'What is the main occasion?',
      help: 'Choose the moment you would most like this fragrance to suit.',
      multiple: true,
      options: [
        { value: 'everyday', label: 'Everyday' },
        { value: 'casual', label: 'Casual' },
        { value: 'date-night', label: 'Date Night' },
        { value: 'formal', label: 'Formal' },
        { value: 'special-occasion', label: 'Special Occasion' }
      ]
    },
    {
      key: 'family',
      title: 'Which scent style do you prefer?',
      help: 'We will look for the fragrance families that feel closest to your preference.',
      multiple: true,
      options: [
        { value: 'fresh', label: 'Fresh' },
        { value: 'woody', label: 'Woody' },
        { value: 'spicy', label: 'Spicy' },
        { value: 'floral', label: 'Floral' },
        { value: 'fruity', label: 'Fruity' },
        { value: 'sweet', label: 'Sweet' },
        { value: 'musky', label: 'Musky' },
        { value: 'oriental-oud', label: 'Oriental / Oud' }
      ]
    },
    {
      key: 'feeling',
      title: 'What feeling do you want?',
      help: 'This refines the ranking using the fragrance families already on each product.',
      options: [
        { value: 'clean-light', label: 'Clean and Light' },
        { value: 'balanced-versatile', label: 'Balanced and Versatile' },
        { value: 'rich-bold', label: 'Rich and Bold' }
      ]
    },
    {
      key: 'budget',
      title: 'What is your budget?',
      help: 'These ranges are based on the prices in the current fragrance collection.',
      multiple: true,
      options: [
        { value: 'under-25', label: '$25 and under' },
        { value: '26-40', label: '$26 – $40' },
        { value: '41-60', label: '$41 – $60' },
        { value: '61-plus', label: '$61 and above' }
      ]
    }
  ];

  const genderTerms = {
    men: ['male'],
    women: ['female'],
    unisex: ['unisex']
  };

  const familyTerms = {
    fresh: ['fresh', 'citrus', 'aquatic', 'ozonic', 'green', 'salty'],
    woody: ['woody', 'oud', 'earthy', 'mossy', 'patchouli', 'sandalwood', 'cedar'],
    spicy: ['spicy', 'warm spicy', 'fresh spicy', 'cinnamon', 'peppery'],
    floral: ['floral', 'white floral', 'rose', 'violet', 'iris', 'jasmine', 'tuberose'],
    fruity: ['fruity', 'coconut'],
    sweet: ['sweet', 'vanilla', 'caramel', 'gourmand'],
    musky: ['musky', 'musk', 'powdery', 'animalic'],
    'oriental-oud': ['oriental', 'oud', 'amber', 'balsamic', 'tobacco', 'leather', 'smoky', 'resinous']
  };

  const feelingTerms = {
    'clean-light': ['fresh', 'citrus', 'aquatic', 'ozonic', 'green', 'salty', 'musky', 'musk'],
    'balanced-versatile': ['fresh', 'woody', 'floral', 'aromatic', 'citrus', 'musky', 'fruity'],
    'rich-bold': ['oud', 'oriental', 'amber', 'balsamic', 'tobacco', 'warm spicy', 'spicy', 'leather', 'smoky']
  };

  const budgetRanges = {
    'under-25': { min: 0, max: 25 },
    '26-40': { min: 26, max: 40 },
    '41-60': { min: 41, max: 60 },
    '61-plus': { min: 61, max: null }
  };

  class FragranceFinder {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-finder-stage]');
      this.results = root.querySelector('[data-finder-results]');
      this.controls = root.querySelector('[data-finder-controls]');
      this.progress = root.querySelector('[data-finder-progress]');
      this.progressValue = root.querySelector('[data-finder-progress-value]');
      this.back = root.querySelector('[data-finder-back]');
      this.next = root.querySelector('[data-finder-next]');
      this.settings = this.readJson('[data-finder-settings]', {});
      this.catalog = this.readJson('[data-finder-catalog]', { products: [] }).products || [];
      this.answers = {};
      this.index = 0;

      this.bindEvents();
      this.renderQuestion();
    }

    readJson(selector, fallback) {
      try {
        const element = this.root.querySelector(selector);
        return element ? JSON.parse(element.textContent) : fallback;
      } catch (error) {
        return fallback;
      }
    }

    bindEvents() {
      this.stage.addEventListener('click', (event) => {
        const answer = event.target.closest('[data-finder-answer]');
        if (!answer) return;
        const question = questions[this.index];
        const value = answer.dataset.value;

        if (question.multiple) {
          const selected = this.getAnswerValues(question.key);
          this.answers[question.key] = selected.includes(value)
            ? selected.filter((selectedValue) => selectedValue !== value)
            : [...selected, value];
        } else {
          this.answers[question.key] = value;
        }

        this.renderQuestion(answer.dataset.value);
      });

      this.back.addEventListener('click', () => {
        if (this.index > 0) {
          this.index -= 1;
          this.renderQuestion();
        }
      });

      this.next.addEventListener('click', () => {
        const question = questions[this.index];
        if (!this.answers[question.key]) return;
        if (this.index === questions.length - 1) {
          this.showResults();
        } else {
          this.index += 1;
          this.renderQuestion();
        }
      });

      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-finder-restart]')) {
          this.restart();
        }
      });
    }

    renderQuestion(focusValue) {
      const question = questions[this.index];
      const selected = this.answers[question.key];
      const selectedValues = this.getAnswerValues(question.key);
      const answerClass = question.options.length === 4 ? ' fi-finder__answers--four' : question.options.length === 5 ? ' fi-finder__answers--five' : '';
      const options = question.options.map((option) => {
        const active = selectedValues.includes(option.value);
        return `
          <button
            class="fi-finder__answer${question.multiple ? ' fi-finder__answer--multiple' : ''}"
            type="button"
            role="${question.multiple ? 'checkbox' : 'radio'}"
            aria-checked="${active}"
            aria-pressed="${active}"
            data-finder-answer
            data-value="${escapeHtml(option.value)}"
          >
            <span class="fi-finder__answer-label">${escapeHtml(option.label)}</span>
          </button>`;
      }).join('');

      this.stage.innerHTML = `
        <div class="fi-finder__question">
          <div class="fi-finder__question-header">
            <p class="fi-finder__question-kicker">Question ${this.index + 1}</p>
            <h2 id="FiFinderQuestion-${this.root.dataset.sectionId}" class="fi-finder__question-title">${escapeHtml(question.title)}</h2>
            <p class="fi-finder__question-help">${escapeHtml(question.help)}</p>
          </div>
          <div class="fi-finder__answers${answerClass}" role="${question.multiple ? 'group' : 'radiogroup'}" aria-labelledby="FiFinderQuestion-${this.root.dataset.sectionId}">
            ${options}
          </div>
        </div>`;

      this.progress.textContent = `Question ${this.index + 1} of ${questions.length}`;
      this.progressValue.style.width = `${((this.index + 1) / questions.length) * 100}%`;
      this.back.disabled = this.index === 0;
      this.next.disabled = question.multiple ? selectedValues.length === 0 : !selected;
      this.next.textContent = this.index === questions.length - 1
        ? (this.settings.viewResultsLabel || 'View results')
        : (this.settings.continueLabel || 'Continue');

      if (focusValue) {
        const selectedButton = this.stage.querySelector(`[data-value="${focusValue}"]`);
        if (selectedButton) selectedButton.focus();
      }
    }

    hasTerm(values, terms) {
      const normalised = values.map(normalise);
      return normalised.some((value) => terms.some((term) => value === normalise(term)));
    }

    getAnswerValues(key) {
      const answer = this.answers[key];
      if (Array.isArray(answer)) return answer;
      return answer ? [answer] : [];
    }

    scoreProduct(product) {
      const genders = product.genders || [];
      const occasions = product.occasions || [];
      const families = product.families || [];
      const matches = { gender: false, occasion: false, family: false, feeling: false, budget: false };
      let score = 0;

      if (!genders.length || !occasions.length || !families.length) return { product, score: -1, matches };

      if (this.answers.gender !== 'no-preference') {
        matches.gender = this.hasTerm(genders, genderTerms[this.answers.gender]);
        if (matches.gender) score += 40;
        else if (this.answers.gender !== 'unisex' && this.hasTerm(genders, ['unisex'])) score += 12;
      }

      const occasionLabels = this.getAnswerValues('occasion')
        .map((value) => questions[1].options.find((option) => option.value === value)?.label)
        .filter(Boolean);
      matches.occasion = occasionLabels.some((label) => occasions.some((occasion) => normalise(occasion) === normalise(label)));
      if (matches.occasion) score += 30;

      const selectedFamilyTerms = this.getAnswerValues('family')
        .flatMap((value) => familyTerms[value] || []);
      matches.family = this.hasTerm(families, selectedFamilyTerms);
      if (matches.family) score += 25;

      matches.feeling = this.hasTerm(families, feelingTerms[this.answers.feeling] || []);
      if (matches.feeling) score += 10;

      const selectedRanges = this.getAnswerValues('budget')
        .map((value) => budgetRanges[value])
        .filter(Boolean);
      matches.budget = selectedRanges.some((range) => product.price >= range.min && (range.max === null || product.price <= range.max));
      if (matches.budget) score += 5;
      if (product.available) score += 4;

      return { product, score, matches };
    }

    getMatches() {
      return this.catalog
        .map((product) => this.scoreProduct(product))
        .filter((item) => item.score >= 0 && (item.matches.occasion || item.matches.family || item.matches.gender))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          if (Number(b.product.available) !== Number(a.product.available)) return Number(b.product.available) - Number(a.product.available);
          return a.product.price - b.product.price;
        })
        .slice(0, 3);
    }

    formatPrice(price) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.settings.currency || 'USD',
          currencyDisplay: 'narrowSymbol'
        }).format(price);
      } catch (error) {
        return `$${Number(price).toFixed(2)}`;
      }
    }

    getFilterUrl() {
      const rawUrl = this.root.dataset.collectionUrl || '/collections/all?view=fragrance';
      const url = new URL(rawUrl, window.location.origin);
      const addValues = (key, values) => (values || []).forEach((value) => url.searchParams.append(key, value));
      const filters = this.settings.filters || {};

      if (this.answers.gender !== 'no-preference') {
        addValues('filter.v.t.shopify.target-gender', filters.gender?.[this.answers.gender]);
      }
      this.getAnswerValues('occasion').forEach((value) => {
        addValues('filter.v.t.shopify.occasion', filters.occasion?.[value]);
      });
      this.getAnswerValues('family').forEach((value) => {
        addValues('filter.v.t.shopify.olfactory-family', filters.family?.[value]);
      });

      const selectedRanges = this.getAnswerValues('budget')
        .map((value) => budgetRanges[value])
        .filter(Boolean);
      if (selectedRanges.length) {
        const minimum = Math.min(...selectedRanges.map((range) => range.min));
        const maximum = selectedRanges.some((range) => range.max === null)
          ? null
          : Math.max(...selectedRanges.map((range) => range.max));
        url.searchParams.set('filter.v.price.gte', minimum.toFixed(2));
        if (maximum !== null) url.searchParams.set('filter.v.price.lte', maximum.toFixed(2));
      }
      url.searchParams.set('view', 'fragrance');
      return `${url.pathname}?${url.searchParams.toString()}`;
    }

    renderCard(match) {
      const { product } = match;
      const image = product.image
        ? `<img class="fi-finder__result-image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="900" height="1125">`
        : '<span class="fi-finder__result-placeholder" aria-hidden="true"></span>';
      const formId = `fi-finder-cart-${product.id}`;
      const buttonLabel = product.available && product.variantId ? 'Add to cart' : 'Sold out';
      const buttonDisabled = product.available && product.variantId ? '' : ' disabled';
      return `
        <article class="fi-finder__result-card">
          <div class="fi-finder__result-media">
            <a class="fi-finder__result-media-link" href="${escapeHtml(product.url)}" aria-label="View ${escapeHtml(product.title)}">
              <span class="fi-finder__result-image-wrap">
                ${image}
              </span>
            </a>
          </div>
          <div class="fi-finder__result-content">
            ${product.vendor ? `<p class="fi-finder__result-vendor">${escapeHtml(product.vendor)}</p>` : ''}
            <h3 class="fi-finder__result-title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
            ${product.families?.length ? `<p class="fi-finder__result-family">${escapeHtml(product.families.slice(0, 4).join(' · '))}</p>` : ''}
            <p class="fi-finder__result-price">${escapeHtml(this.formatPrice(product.price))}</p>
          </div>
          <product-form class="fi-finder__result-cart" data-section-id="${escapeHtml(this.root.dataset.sectionId)}" data-hide-errors="true">
            <form id="${escapeHtml(formId)}" method="post" action="/cart/add" class="form" enctype="multipart/form-data" novalidate="novalidate">
              <input type="hidden" name="id" value="${escapeHtml(product.variantId || '')}">
              <button id="${escapeHtml(formId)}-submit" type="submit" name="add" class="fi-finder__result-action" data-sold-out-message="true"${buttonDisabled}>
                <span>${buttonLabel}</span>
                <span class="sold-out-message hidden">Sold out</span>
                <span class="loading__spinner hidden" aria-hidden="true"></span>
              </button>
            </form>
          </product-form>
        </article>`;
    }

    showResults() {
      const matches = this.getMatches();
      this.stage.hidden = true;
      this.controls.hidden = true;
      this.results.hidden = false;
      this.progress.parentElement.hidden = true;

      const resultContent = matches.length
        ? `<div class="fi-finder__results-grid">${matches.map((match) => this.renderCard(match)).join('')}</div>`
        : `<p class="fi-finder__empty">${escapeHtml(this.settings.emptyText || 'We could not find an exact match yet.')}</p>`;
      this.results.innerHTML = `
        <div class="fi-finder__results-header">
          <h2 class="fi-finder__result-heading">${escapeHtml(this.settings.resultHeading || 'Your fragrance matches')}</h2>
          <p class="fi-finder__results-summary">Curated from our current fragrance collection using your selections.</p>
        </div>
        ${resultContent}
        <div class="fi-finder__results-actions">
          <a class="button button--primary" href="${escapeHtml(this.getFilterUrl())}">${escapeHtml(this.settings.viewAllLabel || 'View all matches')}</a>
          <button class="fi-finder__text-button" type="button" data-finder-restart>${escapeHtml(this.settings.restartLabel || 'Restart quiz')}</button>
        </div>`;
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    restart() {
      this.answers = {};
      this.index = 0;
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.stage.hidden = false;
      this.controls.hidden = false;
      this.progress.parentElement.hidden = false;
      this.renderQuestion();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-fragrance-finder]').forEach((root) => new FragranceFinder(root));
  });
})();


